import React, { Suspense, useEffect } from 'react';

import useScreen from '@lyearn/core/hooks/useScreen';
import useToggle from '@lyearn/core/hooks/useToggle';
import Platform from '@lyearn/core/utils/Platform';
import { Box } from '@lyearn/ui';
import WideCardLoader from '@/modules/content/components/ContentCard/Wide/Loader';
import WideCard from '@/modules/content/components/ContentCard/WideCard';
import { ContentCardTypeEnum } from '@/modules/content/config/cards';
import SurveyStatus from '@/modules/content/modules/survey/components/SurveyStatus';
import { SurveyContentCardFragment } from '@/modules/content/modules/survey/fragments/generated/SurveyContentCard';
import { cardImagePropsConfig } from '@/modules/content/utils/renderContentCard';
import { useSurveyCardContentQuery } from '@/modules/corporate/modules/course/graphql/queries/generated/surveyCardContent';
import SurveyModal from '@/modules/corporate/modules/survey/views/SurveyDetails/components/SurveyModal';
import { PerformanceStatusEnum } from '@/types/schema';

import { surveyWideCardStyles } from './styles';
import { CourseSurveyCardProps } from './types';

const isNative = Platform.OS !== 'web';

const mobileImageProps = {
  height: 98,
  width: 80,
};

function CourseSurveyCard(props: CourseSurveyCardProps) {
  const { courseId, surveyId, onLoad, onSubmit, languageType } = props;
  const [{ fetching, data }] = useSurveyCardContentQuery({
    variables: { id: surveyId, isEmbed: true, languageType },
  });
  const [open, { toggle, off }] = useToggle(false);

  const { isMobile } = useScreen();

  const survey = data?.content as SurveyContentCardFragment | null;

  useEffect(() => {
    if (survey) {
      onLoad(survey.duration);
    }
  }, [onLoad, survey]);

  useEffect(() => {
    if (survey?.performance?.status === PerformanceStatusEnum.Completed) {
      onSubmit();
    }
  }, [onSubmit, survey?.performance?.status]);

  if (fetching) {
    return <WideCardLoader className="py-60" />;
  }

  const renderSurveyCard = () => {
    if (!survey) {
      return null;
    }

    const isCompleted = survey.performance?.status === PerformanceStatusEnum.Completed;

    return (
      <Box className={isCompleted ? undefined : 'cursor-pointer'}>
        <WideCard
          isEmbed
          className={isNative || isMobile ? undefined : surveyWideCardStyles}
          content={survey}
          imageProps={isMobile ? mobileImageProps : cardImagePropsConfig[ContentCardTypeEnum.Wide]}
          renderInfo={() => (isCompleted ? <SurveyStatus /> : <></>)}
          onClick={isCompleted ? undefined : toggle}
        />
      </Box>
    );
  };

  const renderSurveyModal = () => {
    if (!open || !survey) {
      return null;
    }

    return (
      <Suspense fallback={null}>
        <SurveyModal isEmbed parentContentId={courseId} surveyId={surveyId} onClose={off} />
      </Suspense>
    );
  };

  return (
    <>
      {renderSurveyCard()}
      {renderSurveyModal()}
    </>
  );
}

export default React.memo(CourseSurveyCard);
